import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('InvestorDocumentTypeService', 'investor-documents');

export interface InvestorDocumentType {
  id: string;
  name: string;
  isCustom: boolean;
}

const byName = (a: InvestorDocumentType, b: InvestorDocumentType) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Item 11: the persisted, admin-managed Document Type catalog (investor_document_types).
 * Built-ins (is_custom=false) are seeded per org and are not user-deletable; admins can add
 * and remove custom types. RLS scopes reads to the org and writes to admins / edit_investors.
 */
export class InvestorDocumentTypeService {
  /** The org's catalog (built-ins + custom), alphabetized case-insensitively. */
  static async list(): Promise<InvestorDocumentType[]> {
    const { data, error } = await supabase
      .from('investor_document_types')
      .select('id, name, is_custom');
    if (error) {
      log.error('Failed to list document types', {}, error);
      throw error;
    }
    return (data ?? [])
      .map((r) => ({ id: r.id as string, name: r.name as string, isCustom: r.is_custom as boolean }))
      .sort(byName);
  }

  /** Create a custom type. Trims; rejects empty + case-insensitive duplicates. */
  static async create(organizationId: string, name: string): Promise<InvestorDocumentType> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error('Document type name is required');
    }
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('investor_document_types')
      .insert({ organization_id: organizationId, name: trimmed, is_custom: true, created_by: user?.id ?? null })
      .select('id, name, is_custom')
      .single();
    if (error) {
      if (error.code === '23505') {
        throw new Error('This document type already exists');
      }
      log.error('Failed to create document type', { name: trimmed }, error);
      throw error;
    }
    return { id: data.id as string, name: data.name as string, isCustom: data.is_custom as boolean };
  }

  /** How many documents (across investors in the org) currently use this type name. */
  static async usageCount(name: string): Promise<number> {
    const { count, error } = await supabase
      .from('investor_documents')
      .select('id', { count: 'exact', head: true })
      .eq('category', name);
    if (error) {
      log.error('Failed to count document type usage', { name }, error);
      throw error;
    }
    return count ?? 0;
  }

  /** Delete a custom type. Caller must confirm it is unused; built-ins are not deletable. */
  static async remove(id: string): Promise<void> {
    const { error } = await supabase.from('investor_document_types').delete().eq('id', id);
    if (error) {
      log.error('Failed to delete document type', { id }, error);
      throw error;
    }
  }
}
